import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus, Pencil, Trash2, Image as ImageIcon,
  X, RefreshCw,
} from 'lucide-react';
import pb from '../lib/pocketbaseClient';

const SLOTS = [
  { value: 'blog-sidebar-1', label: 'Blog Sidebar 1' },
  { value: 'blog-sidebar-2', label: 'Blog Sidebar 2' },
  { value: 'blog-sidebar-3', label: 'Blog Sidebar 3' },
];

const AdUnit = () => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    slot: '',
    startDate: '',
    endDate: '',
    priority: 0,
    isActive: true,
    link: '',
    images: [],
  });

  const formatDateForPB = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toISOString().replace('T', ' ').slice(0, 19);
  };

  // Fetch ads with error handling
  const { data: ads = [], isLoading, refetch } = useQuery({
    queryKey: ['ads'],
    queryFn: async () => {
      try {
        const records = await pb.collection('ads').getFullList({
          sort: '-created',
        });
        console.log('✅ Fetched ads:', records.length);
        return records;
      } catch (err) {
        console.error('❌ Fetch error:', err);
        // If collection doesn't exist, return empty array
        if (err.status === 404) {
          toast.error('Ads collection not found. Please create it in PocketBase.');
          return [];
        }
        toast.error('Failed to load ads: ' + err.message);
        return [];
      }
    },
    retry: 1,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      try {
        const form = new FormData();
        form.append('title', data.title);
        form.append('slot', data.slot);
        form.append('startDate', formatDateForPB(data.startDate));
        form.append('endDate', formatDateForPB(data.endDate));
        form.append('priority', String(data.priority || 0));
        form.append('isActive', String(data.isActive));
        if (data.link) form.append('link', data.link);
        if (data.images && data.images.length > 0) {
          data.images.forEach((file) => {
            form.append('images', file);
          });
        }

        console.log('📤 Creating ad:', data.title);
        return await pb.collection('ads').create(form);
      } catch (err) {
        console.error('❌ Create error:', err);
        throw err;
      }
    },
    onSuccess: () => {
      toast.success('✅ Ad created successfully');
      resetForm();
      refetch();
      setIsDialogOpen(false);
    },
    onError: (err) => {
      console.error('❌ Create error:', err);
      toast.error('Failed to create ad: ' + err.message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      try {
        // First check if the record exists
        let existing;
        try {
          existing = await pb.collection('ads').getOne(id);
        } catch (checkErr) {
          if (checkErr.status === 404) {
            toast.error('This ad no longer exists. Refreshing list...');
            refetch();
            throw new Error('Ad not found. Please refresh and try again.');
          }
          throw checkErr;
        }

        console.log('✅ Record exists:', existing.id, existing.title);

        const form = new FormData();
        form.append('title', data.title);
        form.append('slot', data.slot);
        form.append('startDate', formatDateForPB(data.startDate));
        form.append('endDate', formatDateForPB(data.endDate));
        form.append('priority', String(data.priority || 0));
        form.append('isActive', String(data.isActive));
        if (data.link) form.append('link', data.link);
        if (data.images && data.images.length > 0) {
          data.images.forEach((file) => {
            form.append('images', file);
          });
        }

        return await pb.collection('ads').update(id, form);
      } catch (err) {
        console.error('❌ Update error:', err);
        throw err;
      }
    },
    onSuccess: () => {
      toast.success('✅ Ad updated successfully');
      resetForm();
      refetch();
      setIsDialogOpen(false);
    },
    onError: (err) => {
      console.error('❌ Update error:', err);
      toast.error('Failed to update ad: ' + err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      try {
        await pb.collection('ads').delete(id);
      } catch (err) {
        if (err.status === 404) {
          toast.error('Ad already deleted. Refreshing...');
          refetch();
          return;
        }
        throw err;
      }
    },
    onSuccess: () => {
      toast.success('✅ Ad deleted');
      refetch();
    },
    onError: (err) => {
      toast.error('Failed to delete ad: ' + err.message);
    },
  });

  const resetForm = () => {
    setFormData({
      title: '',
      slot: '',
      startDate: '',
      endDate: '',
      priority: 0,
      isActive: true,
      link: '',
      images: [],
    });
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEdit = (ad) => {
    console.log('✏️ Editing ad:', ad.id);
    setEditingId(ad.id);
    setFormData({
      title: ad.title || '',
      slot: ad.slot || '',
      startDate: ad.startDate ? ad.startDate.slice(0, 16) : '',
      endDate: ad.endDate ? ad.endDate.slice(0, 16) : '',
      priority: ad.priority || 0,
      isActive: ad.isActive !== undefined ? ad.isActive : true,
      link: ad.link || '',
      images: [],
    });
    setIsDialogOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      setFormData((prev) => ({ ...prev, images: Array.from(files || []) }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title || !formData.slot || !formData.startDate || !formData.endDate) {
      toast.error('Title, slot, start date, and end date are required');
      return;
    }

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    if (start >= end) {
      toast.error('End date must be after start date');
      return;
    }

    const data = { ...formData };
    if (editingId) {
      updateMutation.mutate({ id: editingId, data });
    } else {
      createMutation.mutate(data);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Advertisements</h1>
          <p className="text-sm text-gray-500">Manage all ad campaigns and schedules</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition"
        >
          <Plus className="w-4 h-4" /> Add Ad
        </button>
      </div>

      {/* Ad List */}
      <div className="border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left p-4 font-medium text-gray-600">Title</th>
              <th className="text-left p-4 font-medium text-gray-600">Slot</th>
              <th className="text-left p-4 font-medium text-gray-600">Date Range</th>
              <th className="text-left p-4 font-medium text-gray-600">Priority</th>
              <th className="text-left p-4 font-medium text-gray-600">Images</th>
              <th className="text-left p-4 font-medium text-gray-600">Status</th>
              <th className="text-right p-4 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {ads.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-8 text-gray-500">
                  No ads yet. Click "Add Ad" to create one.
                </td>
              </tr>
            ) : (
              ads.map((ad) => (
                <tr key={ad.id} className="border-b hover:bg-gray-50 transition">
                  <td className="p-4 font-medium text-gray-800">{ad.title}</td>
                  <td className="p-4 text-gray-600">
                    {SLOTS.find((s) => s.value === ad.slot)?.label || ad.slot}
                  </td>
                  <td className="p-4 text-gray-600 text-sm">
                    {ad.startDate && format(new Date(ad.startDate), 'dd MMM yyyy')}{' '}
                    → {ad.endDate && format(new Date(ad.endDate), 'dd MMM yyyy')}
                  </td>
                  <td className="p-4 text-gray-600">{ad.priority || 0}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 text-gray-500">
                      <ImageIcon className="w-4 h-4" />
                      <span className="text-sm">{ad.images?.length || 0}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    {ad.isActive ? (
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEdit(ad)}
                        className="p-1 rounded hover:bg-gray-100 text-gray-600 transition"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('Delete this ad permanently?')) {
                            deleteMutation.mutate(ad.id);
                          }
                        }}
                        className="p-1 rounded hover:bg-red-50 text-red-500 transition"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {isDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setIsDialogOpen(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingId ? 'Edit Ad' : 'Create New Ad'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., Summer Sale"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="slot" className="block text-sm font-medium text-gray-700 mb-1">
                    Slot *
                  </label>
                  <select
                    id="slot"
                    name="slot"
                    value={formData.slot}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select slot</option>
                    {SLOTS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    id="startDate"
                    name="startDate"
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                    End Date *
                  </label>
                  <input
                    id="endDate"
                    name="endDate"
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
                    Priority (higher = shown first)
                  </label>
                  <input
                    id="priority"
                    name="priority"
                    type="number"
                    value={formData.priority}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Active
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="link" className="block text-sm font-medium text-gray-700 mb-1">
                  Destination URL
                </label>
                <input
                  id="link"
                  name="link"
                  type="url"
                  value={formData.link}
                  onChange={handleChange}
                  placeholder="https://example.com/summer-sale"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ad Images (optional – multiple)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleChange}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                />
                {formData.images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.images.map((file, idx) => (
                      <div key={idx} className="relative w-16 h-16 border rounded-lg overflow-hidden">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`preview ${idx}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition"
                          onClick={() => {
                            const newFiles = formData.images.filter((_, i) => i !== idx);
                            setFormData((prev) => ({ ...prev, images: newFiles }));
                          }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  You can select multiple images. The public AdUnit will show them as a carousel.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setIsDialogOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMutation.isLoading || updateMutation.isLoading
                    ? 'Saving...'
                    : editingId
                    ? 'Update'
                    : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdUnit;
